import { useMemo, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Navbar, Nav, Container } from "react-bootstrap";
import {
  FaUserCircle,
  FaEllipsisV,
  FaUsers,
  FaSignOutAlt,
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";

import FiemgLogo from "../../assets/Fiemg_1.svg";
import { clearAuthSession } from "../../services/authSession";
import { PATHS } from "../../routes/paths";
import "./Header.css";

const SINDICATO_HEADER_KEY = "sindicatoHeader";

function onlyDigits(v = "") {
  return String(v).replace(/\D/g, "");
}

function formatCNPJ(cnpj: string) {
  const d = onlyDigits(cnpj);
  if (d.length !== 14) return cnpj || "";
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

function readSindicatoHeaderFromStorage() {
  try {
    const raw = localStorage.getItem(SINDICATO_HEADER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.sigla) return null;
    return { ...parsed, cnpj: formatCNPJ(parsed?.cnpj || "") };
  } catch {
    return null;
  }
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [userName, setUserName] = useState("Nome do Usuário");
  const [isAdmin, setIsAdmin] = useState(false);

  const [sindicatoHeader, setSindicatoHeader] = useState(() =>
    readSindicatoHeaderFromStorage(),
  );

  const syncUserAndPerfil = () => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setUserName(parsedUser?.nome || "Nome do Usuário");
      } catch {
        setUserName("Nome do Usuário");
      }
    } else {
      setUserName("Nome do Usuário");
    }

    const perfil = localStorage.getItem("perfil");
    if (perfil) {
      try {
        const parsedPerfil = JSON.parse(perfil);
        const nome = parsedPerfil?.nome;
        setIsAdmin(nome === "Administrador TI" || nome === "Administrador");
      } catch {
        setIsAdmin(false);
      }
    } else {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    syncUserAndPerfil();
  }, [location.pathname]);

  useEffect(() => {
    const onUpdate = () => setSindicatoHeader(readSindicatoHeaderFromStorage());
    window.addEventListener("sindicatoHeaderUpdated", onUpdate);
    onUpdate();
    return () => window.removeEventListener("sindicatoHeaderUpdated", onUpdate);
  }, [location.pathname]);

  useEffect(() => {
    const onAuthChanged = () => syncUserAndPerfil();
    window.addEventListener("authChanged", onAuthChanged);
    return () => window.removeEventListener("authChanged", onAuthChanged);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsMenuOpen(false);
    clearAuthSession();
    navigate(PATHS.LOGIN);
  };

  const handleManageUsers = () => {
    setIsMenuOpen(false);
    navigate(PATHS.MANAGE_USERS);
  };

  const handleLogoClick = () => navigate(PATHS.HOME);

  const showSindicato = Boolean(
    sindicatoHeader?.sigla && sindicatoHeader?.cnpj,
  );

  const titulo = useMemo(() => {
    if (showSindicato) {
      return `MERITOCRACIA - ${sindicatoHeader.sigla} - CNPJ:${sindicatoHeader.cnpj}`;
    }
    return "MERITOCRACIA";
  }, [showSindicato, sindicatoHeader]);

  return (
    <Navbar bg="primary" variant="dark" expand="lg" className="shadow px-3 header-navbar">
      <Container fluid className="d-flex justify-content-between">
        <div
          className="d-flex align-items-center header-logo-title"
          onClick={handleLogoClick}
          style={{ cursor: "pointer" }}
        >
          <img src={FiemgLogo} alt="FIEMG Logo" className="cursor-pointer fiemg-logo" />
          <span className="header-divider"></span>
          <h1 className="mb-0 fs-4 header-title">
            <i>{titulo}</i>
          </h1>
        </div>

        <Nav className="align-items-center">
          <span className="text-white fw-bold me-2">{userName}</span>
          <FaUserCircle className="fs-3 text-white me-3" />

          <div ref={menuRef} className="menu-container">
            <FaEllipsisV
              data-testid="menu-icon"
              className="fs-5 menu-icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            />

            {isMenuOpen && (
              <div className="dropdown-menu-custom">
                {isAdmin && (
                  <button
                    onClick={handleManageUsers}
                    className="menu-item manage-users-btn"
                    data-testid="manage-users-btn"
                    type="button"
                  >
                    <FaUsers className="menu-icon user-icon" />
                    <span className="text-primary">Gerenciar Usuários</span>
                  </button>
                )}

                <hr className="dropdown-divider" />

                <button onClick={handleLogout} className="menu-item logout-btn" type="button">
                  <FaSignOutAlt className="menu-icon logout-icon" />
                  <span className="text-danger">Sair</span>
                </button>
              </div>
            )}
          </div>
        </Nav>
      </Container>
    </Navbar>
  );
};

export default Header;